import { useClearResultsQuery } from '#pplx/frontend/apps/ask/all-results/hooks/useAllResultsQuery.js';
import { useRWToken } from '#pplx/frontend/apps/ask/providers/hooks/useRWToken.js';
import { useMutateResult } from '#pplx/frontend/apps/ask/providers/ResultsContext.js';
import { deleteThreadMutation, deleteThreadsMutation, removeWidgetFromEntryMutation, } from '#pplx/frontend/apps/ask/queries/mutations/threadMutations.js';
import { getCollectionThreadsQueryKey, getCollectionThreadsQueryKeyPrefix, getSidebarProjectThreadsQueryKeyPrefix, getUserCollectionsQueryKey, } from '#pplx/frontend/apps/ask/queries/query-keys/collectionKeys.js';
import { getSpacePinnedThreadsQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/spacesKeys.js';
import { getSidebarV2InitialQueryKey, getAffectedSidebarProjectThreadQueries, } from '#pplx/frontend/apps/ask/queries/query-keys/threadKeys.js';
import { RECENT_ITEMS_KEY, getPinnedItemsQueryKey, getPinnedThreadsQueryKey, getScheduledComputerTasksQueryKey, getThreadsQueryKey, } from '#pplx/frontend/apps/ask/queries/query-keys/threadKeys.js';
import { isWidgetData } from '#pplx/frontend/apps/ask/search/search-accessory/search-accessory-types/types.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
import { useDeleteEntry } from '#pplx/frontend/stream/stream.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
// Lets surfaces that keep their own copy of a thread observe its deletion, whoever deleted it.
export const DELETE_THREAD_MUTATION_KEY = ['deleteThread'];
export const DELETE_THREADS_MUTATION_KEY = ['deleteThreads'];
export const useThreadMutations = ({ reason, deleteThreadsExecutor, }) => {
    const queryClient = useQueryClient();
    const clearResultsQuery = useClearResultsQuery();
    const deleteEntry = useDeleteEntry();
    const mutateResult = useMutateResult();
    const rwToken = useRWToken();
    const { mutate: removeWidgetFromEntry } = useMutation({
        mutationKey: ['removeWidget'],
        mutationFn: async ({ entryUUID, data, }) => {
            if (isWidgetData(data) && data.url) {
                return removeWidgetFromEntryMutation({
                    backendUUID: entryUUID,
                    url: data.url,
                    rwToken,
                    reason,
                });
            }
            else {
                // TODO: handle case of removing knowledge cards
                return Promise.resolve();
            }
        },
        onMutate: async ({ entryUUID, data, }) => {
            await queryClient.cancelQueries({
                queryKey: QueryClientHelper.makeQueryKey('results'),
            });
            mutateResult(entryUUID, (message) => {
                if (!isWidgetData(data)) {
                    return message;
                }
                return {
                    ...message,
                    widget_data: message.widget_data?.filter((widget) => widget.url !== data.url),
                };
            });
        },
    });
    const { mutate: deleteThread, mutateAsync: deleteThreadAsync } = useMutation({
        mutationKey: DELETE_THREAD_MUTATION_KEY,
        mutationFn: async ({ entryUUID, contextUUID, }) => {
            await deleteThreadMutation({
                entryUUID,
                rwToken,
                reason,
                contextUUID,
            });
        },
        onMutate: async ({ entryUUID, collectionSlug }) => {
            const baseThreadsKey = getThreadsQueryKey();
            await queryClient.cancelQueries({ queryKey: baseThreadsKey });
            await queryClient.cancelQueries({
                queryKey: getUserCollectionsQueryKey(),
            });
            const previousThreads = queryClient.getQueriesData({
                queryKey: baseThreadsKey,
            });
            const previousCollections = queryClient.getQueryData(getUserCollectionsQueryKey());
            // optimistic update of all thread list caches (including filtered variants like asi)
            queryClient.setQueriesData({ queryKey: baseThreadsKey }, (oldData) => {
                if (!oldData)
                    return oldData;
                return {
                    ...oldData,
                    pages: oldData.pages.map((page) => page.filter((thread) => thread.uuid !== entryUUID)),
                };
            });
            // optimistic update of the collection
            if (collectionSlug) {
                queryClient.setQueryData(getUserCollectionsQueryKey(), 
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY_MIGRATION: Please fix in passing
                (oldData) => {
                    oldData = oldData || { pages: [], pageParams: [] };
                    const updatedPages = oldData.pages.map((page) => {
                        return page.map((collection) => {
                            return collection.slug === collectionSlug
                                ? {
                                    ...collection,
                                    thread_count: (collection.thread_count ?? 0) - 1,
                                }
                                : collection;
                        });
                    });
                    return {
                        ...oldData,
                        pages: updatedPages,
                    };
                });
                queryClient.setQueriesData({
                    queryKey: getCollectionThreadsQueryKeyPrefix(collectionSlug),
                    exact: false,
                }, 
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY_MIGRATION: Please fix in passing
                (oldData) => {
                    if (!oldData?.pages)
                        return oldData;
                    return {
                        ...oldData,
                        pages: oldData.pages.map((page) => page.filter((thread) => thread.uuid !== entryUUID)),
                    };
                });
            }
            return { previousThreads, previousCollections };
        },
        onSettled: (_, error, { entryUUID, contextUUID, callback }, context) => {
            if (error) {
                logger.error('[useThreadMutations] delete thread failed', {
                    entryUUID,
                    contextUUID,
                    reason,
                    error,
                });
                // Restore all thread list caches from snapshot
                for (const [key, data] of context?.previousThreads ?? []) {
                    queryClient.setQueryData(key, data);
                }
                queryClient.setQueryData(getUserCollectionsQueryKey(), context?.previousCollections);
            }
            else {
                queryClient.invalidateQueries({ queryKey: getThreadsQueryKey() });
                queryClient.invalidateQueries({ queryKey: getSidebarV2InitialQueryKey() });
                void queryClient.invalidateQueries(getAffectedSidebarProjectThreadQueries({
                    entryUUIDs: [entryUUID],
                    contextUUIDs: [contextUUID],
                }));
                queryClient.invalidateQueries({ queryKey: getSidebarProjectThreadsQueryKeyPrefix() });
                queryClient.invalidateQueries({
                    queryKey: getUserCollectionsQueryKey(),
                });
                queryClient.invalidateQueries({
                    queryKey: getCollectionThreadsQueryKey(),
                });
                // Refresh pinned-thread caches after single-thread deletes.
                queryClient.invalidateQueries({
                    queryKey: getSpacePinnedThreadsQueryKey(),
                });
                queryClient.invalidateQueries({
                    queryKey: getPinnedThreadsQueryKey(),
                });
                queryClient.invalidateQueries({
                    queryKey: getPinnedItemsQueryKey(),
                });
                queryClient.invalidateQueries({
                    queryKey: QueryClientHelper.makeQueryKey(RECENT_ITEMS_KEY),
                });
                queryClient.invalidateQueries({
                    queryKey: QueryClientHelper.makeEphemeralQueryKey('/rest/thread/recurring_tasks'),
                });
                queryClient.invalidateQueries({
                    queryKey: getScheduledComputerTasksQueryKey(),
                });
                deleteEntry(entryUUID);
                clearResultsQuery(entryUUID);
                callback?.();
            }
        },
    });
    const { mutate: deleteThreads } = useMutation({
        mutationKey: DELETE_THREADS_MUTATION_KEY,
        mutationFn: async ({ entryUUIDs, contextUUIDs, }) => {
            if (deleteThreadsExecutor) {
                return await deleteThreadsExecutor({ entryUUIDs, contextUUIDs });
            }
            return await deleteThreadsMutation({
                entryUUIDs,
                contextUUIDs,
                rwToken,
                reason,
            });
        },
        onMutate: async ({ contextUUIDs, entryUUIDs }) => {
            const baseThreadsKey = getThreadsQueryKey();
            await queryClient.cancelQueries({ queryKey: baseThreadsKey });
            const previousThreads = queryClient.getQueriesData({
                queryKey: baseThreadsKey,
            });
            const previousCollections = queryClient.getQueryData(getUserCollectionsQueryKey());
            // optimistic update of all thread list caches (including filtered variants like asi)
            queryClient.setQueriesData({ queryKey: baseThreadsKey }, (oldData) => {
                if (!oldData)
                    return oldData;
                return {
                    ...oldData,
                    pages: oldData.pages.map((page) => page.filter((thread) => !entryUUIDs.includes(thread.uuid) &&
                        !(contextUUIDs ?? []).includes(thread.context_uuid))),
                };
            });
            return { previousThreads, previousCollections };
        },
        onSettled: (failures, error, { cleanupItems, entryUUIDs, contextUUIDs, callback }, context) => {
            if (error) {
                logger.error('[useThreadMutations] delete threads failed', {
                    entryUUIDs,
                    contextUUIDs,
                    reason,
                    error,
                });
                for (const [key, data] of context?.previousThreads ?? []) {
                    queryClient.setQueryData(key, data);
                }
            }
            else {
                queryClient.invalidateQueries({ queryKey: getThreadsQueryKey() });
                queryClient.invalidateQueries({ queryKey: getSidebarV2InitialQueryKey() });
                void queryClient.invalidateQueries(getAffectedSidebarProjectThreadQueries({ entryUUIDs, contextUUIDs }));
                queryClient.invalidateQueries({ queryKey: getSidebarProjectThreadsQueryKeyPrefix() });
                queryClient.invalidateQueries({
                    queryKey: getUserCollectionsQueryKey(),
                });
                queryClient.invalidateQueries({
                    queryKey: getCollectionThreadsQueryKey(),
                });
                // Refresh pinned-thread caches after bulk thread deletes.
                queryClient.invalidateQueries({
                    queryKey: getSpacePinnedThreadsQueryKey(),
                });
                queryClient.invalidateQueries({
                    queryKey: getPinnedThreadsQueryKey(),
                });
                queryClient.invalidateQueries({
                    queryKey: getPinnedItemsQueryKey(),
                });
                queryClient.invalidateQueries({
                    queryKey: QueryClientHelper.makeQueryKey(RECENT_ITEMS_KEY),
                });
                queryClient.invalidateQueries({
                    queryKey: QueryClientHelper.makeEphemeralQueryKey('/rest/thread/recurring_tasks'),
                });
                queryClient.invalidateQueries({
                    queryKey: getScheduledComputerTasksQueryKey(),
                });
                // skip entry-state cleanup for threads whose delete failed; their rows
                // are restored and their stream/results state must stay intact
                const failedKeys = new Set((failures ?? []).flatMap((failure) => [failure.context_uuid, failure.entry_uuid].filter((key) => Boolean(key))));
                const cleanupTargets = cleanupItems ?? entryUUIDs.map((entryUUID) => ({ entryUUID }));
                const cleanupEntryUuids = cleanupTargets
                    .filter((item) => item.entryUUID &&
                    !failedKeys.has(item.entryUUID) &&
                    !(item.contextUUID && failedKeys.has(item.contextUUID)))
                    .map((item) => item.entryUUID);
                for (const entryUUID of new Set(cleanupEntryUuids)) {
                    deleteEntry(entryUUID);
                    clearResultsQuery(entryUUID);
                }
                callback?.();
            }
        },
    });
    return useMemo(() => ({
        removeWidgetFromEntry,
        deleteThread,
        deleteThreadAsync,
        deleteThreads,
    }), [deleteThread, deleteThreadAsync, deleteThreads, removeWidgetFromEntry]);
};
//# sourceMappingURL=useThreadMutations.js.map